import React, { useEffect } from 'react';
import moment, { Moment } from 'moment';
import { useForm } from 'react-hook-form';
import { CircularProgress, type ModalProps } from '@mui/material';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { AddCircleOutline, CancelOutlined } from '@mui/icons-material';

import { useMarkInvoiceAsPaidMutation } from 'generated/graphql';
import { Modal, ModalContents, ModalTitle, ModalButton } from 'components/Modal/Modal';
import { controlledTextField } from 'components/ReactHookForm/controlledTextField';
import { controlledFileUpload } from 'components/ReactHookForm/controlledFileUpload';
import { controlledDatePicker } from 'components/ReactHookForm/controlledDatePicker';
import { START_DATE_OF_BATCH_FEATURE } from 'containers/GivingAccountPage/AddGivingAccountFundingModal';

type FieldValues = {
  processedAt: Moment;
  note: string;
  paymentDocumentURL: string;
};

// @ts-expect-error something off with the types
const ProcessedAtInput = controlledDatePicker<FieldValues>()('processedAt', {
  rules: {
    required: true,
    validate: {
      minDate: (date) => moment.utc(START_DATE_OF_BATCH_FEATURE).isSameOrBefore(date),
      maxDate: (date) => moment.utc().isSameOrAfter(moment.utc(date).startOf('day')),
    },
  },
});

const NoteInput = controlledTextField<FieldValues>()('note', {
  rules: { required: true },
});

const PaymentDocumentUrlInput = controlledFileUpload<FieldValues>()('paymentDocumentURL', {
  rules: { required: true },
});

export const MarkInvoiceAsPaidModal = ({
  invoiceId,
  style,
  onClickClose = (): void => {},
  ...props
}: {
  invoiceId: string;
  onClickClose: () => void;
} & Omit<ModalProps, 'children'>): JSX.Element => {
  const {
    handleSubmit: reactHookFormHandleSubmit,
    formState: { errors, isValid, isSubmitting },
    control,
    reset,
  } = useForm<FieldValues>({
    mode: 'onChange',
    defaultValues: {
      processedAt: new Date(),
      note: '',
      paymentDocumentURL: '',
    },
  });
  const [markInvoiceAsPaidMutation, { error: markInvoiceAsPaidMutationError }] =
    useMarkInvoiceAsPaidMutation({
      refetchQueries: ['givingAccountSummary', 'givingAccountTransactions', 'invoices'],
    });

  useEffect(() => {
    reset();
  }, [props.open]);

  const handleSubmit = reactHookFormHandleSubmit(async (data) => {
    if (!isValid) {
      return;
    }
    await markInvoiceAsPaidMutation({
      variables: {
        invoiceId,
        note: data.note,
        paymentDocumentURL: data.paymentDocumentURL,
        processedAt: data.processedAt.toISOString(),
      },
    });
    onClickClose();
  });

  return (
    <Modal
      disableAutoFocus
      style={{
        alignItems: 'center',
        justifyContent: 'center',
        ...style,
      }}
      {...props}
    >
      <ModalContents
        onClickClose={onClickClose}
        body={
          <>
            <ModalTitle>Mark Invoice as Paid</ModalTitle>
            <Box paddingTop={4}>
              <form onSubmit={handleSubmit}>
                <Stack spacing={3}>
                  <ProcessedAtInput
                    controllerProps={{ control }}
                    label="Processed at"
                    inputFormat="yyyy-MM-DD"
                    disableFuture
                  />
                  <NoteInput
                    controllerProps={{ control }}
                    helperText={
                      errors.note?.message ||
                      'Internal notes for the Deed Finance team. Not visible to customers'
                    }
                    multiline
                    minRows={3}
                    label="Notes"
                    type="text"
                  />
                  <PaymentDocumentUrlInput
                    controllerProps={{ control }}
                    helperText={
                      errors.paymentDocumentURL?.message ||
                      `The screenshot/print out of the confirmation from the payment processor`
                    }
                    linkPreview
                    s3Acl="private"
                    s3Bucket="secure"
                    label="Payment document"
                  />
                  {markInvoiceAsPaidMutationError && (
                    <Alert severity="error">
                      Failed to mark invoice as paid:
                      <br />
                      <br />
                      {markInvoiceAsPaidMutationError?.message}
                    </Alert>
                  )}
                </Stack>
              </form>
            </Box>
          </>
        }
        actions={
          <>
            <ModalButton
              variant="outlined"
              disabled={isSubmitting}
              onClick={(): void => {
                onClickClose();
              }}
              endIcon={<CancelOutlined />}
            >
              Cancel
            </ModalButton>
            &nbsp;
            <ModalButton
              disabled={isSubmitting}
              onClick={handleSubmit}
              endIcon={<AddCircleOutline />}
            >
              {isSubmitting ? <CircularProgress size={21} /> : 'Mark as Paid'}
            </ModalButton>
          </>
        }
      />
    </Modal>
  );
};
